/**
 * EstadoSection — diagnóstico de preparación (MVP-02C §3.2).
 *
 * Traduce el reporte del backend en un recorrido con estado y motivo por
 * requisito, con acceso directo a la sección que lo resuelve.
 */
import PropTypes from "prop-types";
import { AlertTriangle, RotateCw } from "lucide-react";

import { ProcessSteps } from "../../../../../components/ui/index.js";

const SECTION_LABEL = {
    direcciones: "Ir a Direcciones",
    embalajes: "Ir a Embalajes",
    tarifas: "Ir a Tarifas",
    calendario: "Ir a Calendario y plazos",
};

const formatFecha = (valor) =>
    valor ? new Date(valor).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }) : null;

export default function EstadoSection({ readiness, onNavigate, onRetry }) {
    if (readiness.state === "loading") {
        return (
            <div className="flex flex-col gap-2" aria-busy="true">
                <div className="h-14 rounded-lg bg-base-300/60 animate-pulse" />
                <div className="h-14 rounded-lg bg-base-300/60 animate-pulse" />
                <div className="h-14 rounded-lg bg-base-300/60 animate-pulse" />
                <span className="sr-only">Cargando diagnóstico…</span>
            </div>
        );
    }

    if (readiness.state === "error" || !readiness.data) {
        return (
            <div role="alert" className="flex flex-col items-start gap-3 py-6">
                <p className="text-sm text-error">No se pudo cargar el diagnóstico.</p>
                <button type="button" className="btn btn-sm gap-1" onClick={onRetry}>
                    <RotateCw className="w-4 h-4" /> Reintentar
                </button>
            </div>
        );
    }

    const { requirements, last_modified_at: lastModifiedAt } = readiness.data;

    const steps = requirements.map((req) => ({
        id: req.id,
        label: req.label,
        state: req.ok ? "done" : "blocked",
        reason: req.ok ? undefined : req.reason,
        action: !req.ok && req.cta && SECTION_LABEL[req.cta]
            ? { label: SECTION_LABEL[req.cta], onClick: () => onNavigate(req.cta) }
            : undefined,
    }));

    return (
        <div className="rounded-xl bg-base-100 border border-base-300 shadow-sm p-4 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
                <h2 className="font-semibold">Requisitos para cotizar envío físico</h2>
                <button type="button" onClick={onRetry} className="btn btn-ghost btn-xs gap-1">
                    <RotateCw className="w-3 h-3" /> Actualizar
                </button>
            </div>

            <ProcessSteps steps={steps} />

            {lastModifiedAt && (
                <p className="text-xs text-base-content/50 border-t border-base-300 pt-3">
                    Última modificación relevante: {formatFecha(lastModifiedAt)}.
                </p>
            )}

            <div className="flex items-start gap-2 text-xs text-base-content/60 bg-base-200 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 shrink-0 text-info" />
                <p>
                    Ningún dato de aquí sustituye una tarifa real de Correos, medidas reales
                    de embalaje ni el calendario real de manipulación. Cárgalos solo cuando
                    los tengas confirmados.
                </p>
            </div>
        </div>
    );
}

EstadoSection.propTypes = {
    readiness: PropTypes.shape({
        state: PropTypes.oneOf(["loading", "ready", "error"]).isRequired,
        data: PropTypes.object,
    }).isRequired,
    onNavigate: PropTypes.func.isRequired,
    onRetry: PropTypes.func.isRequired,
};
